import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSearchbar,
  IonSpinner,
  IonToolbar,
} from '@ionic/react';
import {
  Cell,
  ColumnDef,
  Header,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import Avatar from 'components/Avatar/Avatar';
import WorkOrderFilters from 'containers/WorkOrderFilters';
import { FilterType } from 'containers/WorkOrderFilters/FilterType';
import { ReducerState as FiltersState } from 'containers/WorkOrderFilters/reducer';
import { parseAddress } from 'hooks/useLocation';
import { useStatus as fetchStatus } from 'hooks/useStatus';
import { Status } from 'interfaces/Status';
import {
  caretDownOutline as sortDescIcon,
  caretUpOutline as sortAscIcon,
  reorderTwoOutline as reorderIcon,
} from 'ionicons/icons';
import moment from 'moment';
import { WorkOrderSearchResult } from 'pages/WorkOrders/graphql';
import React, { CSSProperties, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';

const DESKTOP_STATUS_ORDER: Status[] = [
  Status.Submitted,
  Status.Started,
  Status.Completed,
  Status.Denied,
  Status.Cancelled,
  Status.Reopened,
  Status.OnHold,
];

const hashToColor = (key: string) => {
  if (!key) return '#6b7280';
  const hash = [...key].reduce((acc, c) => {
    const n = c.charCodeAt(0);
    return (acc << 5) - acc + n;
  }, 0);
  const hex = ((hash & 0xffffff) + 0x1000000).toString(16).slice(-6);
  return `#${hex}`;
};

const useStyles = createUseStyles({
  toolbarRow: {
    '--padding-start': '12px',
    '--padding-end': '12px',
    '--min-height': 'auto',
    '&::part(content)': {
      alignItems: 'center',
    },
  },
  toolbarInner: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    paddingTop: 8,
    paddingBottom: 8,
  },
  search: {
    flex: '1 1 240px',
    minWidth: 200,
    maxWidth: 420,
    padding: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    flex: '1 1 auto',
  },
  chip: {
    border: '1px solid var(--ion-color-step-250, #d7d8da)',
    borderRadius: 999,
    padding: '6px 14px',
    margin: '0 8px 0 0',
    fontSize: '0.8125rem',
    fontWeight: 500,
    background: 'var(--ion-background-color, #fff)',
    color: 'var(--ion-text-color)',
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  },
  chipActive: {
    background: 'var(--ion-text-color, #111)',
    color: 'var(--ion-background-color, #fff)',
    borderColor: 'var(--ion-text-color, #111)',
  },
  chipDisabled: {
    opacity: 0.5,
  },
  moreFiltersBtn: {
    position: 'relative',
  },
  filterIndicator: {
    position: 'absolute',
    height: '0.625em',
    width: '0.625em',
    top: '1px',
    right: '1px',
  },
  tableWrap: {
    overflow: 'auto',
    padding: '0 16px 24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '0.875rem',
  },
  th: {
    textAlign: 'left',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--ion-color-medium)',
    padding: '12px 10px',
    borderBottom: '1px solid var(--ion-color-step-150, #e8e9ea)',
    whiteSpace: 'nowrap',
    background: 'var(--ion-background-color, #fff)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      width: '1px',
      height: '50%',
      background: 'var(--ion-color-step-150, #e8e9ea)',
    },
  },
  thLabel: {
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ion-color-medium)',
    background: 'var(--ion-background-color, #fff)',
    verticalAlign: 'middle',
  },
  thDragIcon: {
    color: 'var(--ion-color-medium)',
    backgroundColor: 'transparent',
    marginRight: '5px',
    verticalAlign: 'middle',
    transform: 'rotate(-90deg)',
  },
  thSortable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  td: {
    padding: '14px 10px',
    borderBottom: '1px solid var(--ion-color-step-100, #f1f2f3)',
    verticalAlign: 'middle',
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': {
      background: 'var(--ion-color-light)',
    },
  },
  woId: {
    fontWeight: 700,
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  titlePrimary: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleSecondary: {
    fontSize: '0.8125rem',
    color: 'var(--ion-color-medium)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ion-color-medium)',
  },
  teamBadge: {
    borderRadius: 6,
    border: '1px solid var(--ion-color-step-250, #d7d8da)',
  },
  emptyBadge: {
    display: 'inline-block',
    borderRadius: 6,
    border: '1px dashed var(--ion-color-step-250, #d7d8da)',
  },
  dotRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  assigneeCell: {
    display: 'flex',
    alignItems: 'center',
  },
  assigneeName: {
    marginLeft: '8px',
    fontSize: '0.750rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dateStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  dateRel: {
    fontSize: '0.75rem',
    color: 'var(--ion-color-medium)',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  loadingRow: {
    textAlign: 'center',
    padding: 24,
  },
  sortIconsContainer: {
    display: 'inline-flex',
    flexDirection: 'column',
    verticalAlign: 'middle',
    marginLeft: '5px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  sortActive: {
    opacity: 1,
  },
  sortInactive: {
    opacity: 0.3,
  },
  sortDirActive: {
    color: 'var(--ion-color-primary)',
  },
});

export interface WorkOrdersSearchDesktopProps {
  searchResults: WorkOrderSearchResult[];
  loading: boolean;
  query: string | undefined;
  inputValue: string | undefined;
  setInputValue: (v: string | undefined) => void;
  setQuery: (v: string | undefined) => void;
  hasNextPage: boolean;
  loadMore: () => Promise<unknown>;
  hasChecked: boolean;
  statusOptions: { status: Status; count: number }[];
  filters: FiltersState;
  clear: (filter: FilterType) => void;
  update: (filter: FilterType, criteria: Map<string, boolean>) => void;
}

type SortableThClasses = Pick<
  ReturnType<typeof useStyles>,
  | 'th'
  | 'thSortable'
  | 'sortIconsContainer'
  | 'sortActive'
  | 'sortInactive'
  | 'sortDirActive'
  | 'thLabel'
  | 'thDragIcon'
  | 'td'
>;

const DraggableTableHeader = ({
  header,
  classes,
}: {
  header: Header<WorkOrderSearchResult, unknown>;
  classes: SortableThClasses;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });
  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    background: isDragging ? 'var(--ion-color-light)' : 'transparent',
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  const draggableStyle: CSSProperties = { cursor: isDragging ? 'grabbing' : 'grab' };

  return (
    <th colSpan={header.colSpan} ref={setNodeRef} style={style} className={classes.th}>
      <button {...attributes} {...listeners} className={classes.thDragIcon} style={draggableStyle}>
        <IonIcon icon={reorderIcon} style={{ width: '24px', height: '24px' }} />
      </button>
      <span
        onClick={() => header.column.toggleSorting()}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span className={classes.thLabel}>
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        <span className={clsx(classes.sortIconsContainer)}>
          <IonIcon
            icon={sortAscIcon}
            className={clsx(
              header.column.getIsSorted() === 'asc' ? classes.sortDirActive : classes.sortInactive
            )}
          />
          <IonIcon
            icon={sortDescIcon}
            className={clsx(
              header.column.getIsSorted() === 'desc' ? classes.sortDirActive : classes.sortInactive
            )}
          />
        </span>
      </span>
    </th>
  );
};

const DragAlongCell = ({
  cell,
  children,
  classes,
}: {
  cell: Cell<WorkOrderSearchResult, unknown>;
  children: React.ReactNode;
  classes: SortableThClasses;
}) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    background: isDragging ? 'var(--ion-color-light)' : 'transparent',
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: cell.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <td style={style} ref={setNodeRef} className={classes.td}>
      {children}
    </td>
  );
};

type ColumnSort = { id: string; desc: boolean };
type SortingState = ColumnSort[];

const WorkOrdersSearchDesktop: React.FC<WorkOrdersSearchDesktopProps> = ({
  searchResults,
  loading,
  query,
  inputValue,
  setInputValue,
  setQuery,
  hasNextPage,
  loadMore,
  hasChecked,
  statusOptions,
  filters,
  clear,
  update,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<WorkOrderSearchResult>[]>(
    () => [
      {
        accessorFn: (row) => row.titleDisplay,
        id: 'title',
        header: 'TITLE & ADDRESS',
        sortingFn: 'alphanumeric',
        // sortDescFirst: true,
      },
      { accessorKey: 'status', id: 'status', header: 'STATUS', sortingFn: 'text' },
      {
        accessorFn: (row) => row.category?.name || '',
        id: 'category',
        header: 'CATEGORY',
        sortingFn: 'text',
      },
      { accessorFn: (row) => row.team?.name || '', id: 'team', header: 'TEAM', sortingFn: 'text' },
      {
        accessorFn: (row) => row.assignee?.name || '',
        id: 'assignee',
        header: 'ASSIGNEE',
        sortingFn: 'text',
      },
      { accessorKey: 'createdAt', id: 'createdAt', header: 'SUBMITTED', sortingFn: 'basic' },
    ],
    []
  );
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => columns.map((c) => c.id!));

  const table = useReactTable({
    data: searchResults,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnOrder,
      sorting,
    },
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setSorting,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const availableStatuses = useMemo(() => {
    return new Map(statusOptions.map((s) => [s.status, s]));
  }, [statusOptions]);
  const isAllStatuses = useMemo(() => [...filters.statuses.values()].every((v) => !v), [
    filters.statuses,
  ]);

  const handleStatusChip = (status: Status | 'all') => {
    if (status === 'all') {
      clear(FilterType.Status);
      return;
    }
    const keys = [...filters.statuses.keys()];
    const currentFilters = filters.statuses;

    // filter status chips are toggleable
    const next = new Map(
      keys.map((k) => [k, k === status ? !currentFilters.get(k) : currentFilters.get(k) ?? false])
    );
    update(FilterType.Status, next);
  };

  const chipActive = (status: Status | 'all') => {
    if (status === 'all') return isAllStatuses;
    return filters.statuses.get(status) === true;
  };

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((prevColumnOrder) => {
        const oldIndex = prevColumnOrder.indexOf(active.id as string);
        const newIndex = prevColumnOrder.indexOf(over.id as string);
        return arrayMove(prevColumnOrder, oldIndex, newIndex); // this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <IonToolbar className={classes.toolbarRow}>
        <div className={classes.toolbarInner}>
          <IonSearchbar
            className={classes.search}
            debounce={1000}
            mode="md"
            placeholder="Search work orders..."
            style={{ '--border-radius': '100px' }}
            value={inputValue}
            onIonInput={(e) => setInputValue(e.target.value || undefined)}
            onIonChange={(e) => setQuery(e.detail.value || undefined)}
          />

          <div className={classes.chips}>
            <button
              type="button"
              className={clsx(classes.chip, chipActive('all') && classes.chipActive)}
              onClick={() => handleStatusChip('all')}
            >
              All
            </button>
            {DESKTOP_STATUS_ORDER.map((status) => {
              const { label } = fetchStatus(status);
              return (
                <button
                  key={status}
                  type="button"
                  className={clsx(
                    classes.chip,
                    chipActive(status) && classes.chipActive,
                    !availableStatuses.get(status) &&
                      !filters.statuses.get(status) &&
                      classes.chipDisabled
                  )}
                  onClick={() => handleStatusChip(status)}
                  // don't disable if status was already active before becoming unavailable
                  disabled={!availableStatuses.get(status) && !filters.statuses.get(status)}
                >
                  {label}
                </button>
              );
            })}
            <WorkOrderFilters.Toggle hasChecked={hasChecked} />
          </div>
        </div>
      </IonToolbar>

      {!(searchResults.length || loading) && (
        <IonToolbar style={{ '--min-height': 0 }}>
          <div
            style={{
              padding: '0 16px 12px',
              fontSize: '0.875rem',
              color: 'var(--ion-color-medium)',
            }}
          >
            {query
              ? `0 matches for search "${query}"${hasChecked ? ' and active filters' : ''}`
              : '0 matches for active filters'}
          </div>
        </IonToolbar>
      )}

      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              {table.getHeaderGroups().map((headerGroup) => (
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => (
                    <DraggableTableHeader key={header.id} header={header} classes={classes} />
                  ))}
                </SortableContext>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const wo = row.original;
              const { color: statusColor, label: statusLabel } = fetchStatus(wo.status);
              const badgeBg = `${statusColor}22`;
              const addr = wo.location?.address ? parseAddress(wo.location.address) : '';
              const catColor = hashToColor(wo.category?.id || wo.category?.name || 'x');
              const teamColor = hashToColor(wo.team?.id || wo.team?.name || 'y');

              return (
                <tr
                  key={wo.id}
                  className={classes.row}
                  onClick={() => {
                    window.open(
                      history.createHref({ pathname: `/workorders/details/${wo.id}` }),
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <SortableContext
                      key={cell.id}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {cell.column.id === 'title' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          <div className={classes.titleStack}>
                            <span className={classes.titlePrimary}>{wo.titleDisplay}</span>
                            {addr ? <span className={classes.titleSecondary}>{addr}</span> : null}
                          </div>
                        </DragAlongCell>
                      )}
                      {cell.column.id === 'status' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          <span
                            className={classes.statusBadge}
                            style={{ backgroundColor: badgeBg, color: statusColor }}
                          >
                            {statusLabel}
                          </span>
                        </DragAlongCell>
                      )}
                      {cell.column.id === 'category' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          <div className={classes.dotRow}>
                            {wo.category?.name ? (
                              <span className={clsx(classes.badge)}>
                                <span
                                  className={classes.dot}
                                  style={{ backgroundColor: catColor }}
                                />
                                {wo.category?.name || '—'}
                              </span>
                            ) : (
                              <span className={clsx(classes.badge, classes.emptyBadge)}>
                                No category
                              </span>
                            )}
                          </div>
                        </DragAlongCell>
                      )}
                      {cell.column.id === 'team' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          <div className={classes.dotRow}>
                            {wo.team?.name ? (
                              <span className={clsx(classes.badge, classes.teamBadge)}>
                                <span
                                  className={classes.dot}
                                  style={{ backgroundColor: teamColor }}
                                />
                                {wo.team?.name || '—'}
                              </span>
                            ) : (
                              <span className={clsx(classes.badge, classes.emptyBadge)}>
                                No team
                              </span>
                            )}
                          </div>
                        </DragAlongCell>
                      )}
                      {cell.column.id === 'assignee' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          {wo.assignee?.name ? (
                            <div className={classes.assigneeCell}>
                              <Avatar alt={wo.assignee.name} style={{ width: 24, height: 24 }} />
                              <span className={classes.assigneeName}>{wo.assignee.name}</span>
                            </div>
                          ) : (
                            <span className={clsx(classes.badge, classes.emptyBadge)}>
                              Unassigned
                            </span>
                          )}
                        </DragAlongCell>
                      )}
                      {cell.column.id === 'createdAt' && (
                        <DragAlongCell key={cell.id} cell={cell} classes={classes}>
                          <div className={classes.dateStack}>
                            <span>{moment(wo.createdAt).format('L')}</span>
                            <span className={classes.dateRel}>
                              {moment(wo.createdAt).fromNow()}
                            </span>
                          </div>
                        </DragAlongCell>
                      )}
                    </SortableContext>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && searchResults.length === 0 && (
          <div className={classes.loadingRow}>
            <IonSpinner name="crescent" />
          </div>
        )}

        <IonInfiniteScroll
          disabled={!hasNextPage}
          onIonInfinite={async (e) => {
            const target = e.target;
            try {
              await loadMore();
            } finally {
              target.complete();
            }
          }}
        >
          <IonInfiniteScrollContent loadingSpinner="crescent" />
        </IonInfiniteScroll>
      </div>
    </DndContext>
  );
};

export default WorkOrdersSearchDesktop;
